import { underlyingsApi } from 'apiConnector/underlyings.old';
import { FormGlobalInfo, useFormGlobalInfo } from 'components/common-form/hooks/use-form-global-info';
import { useFieldMetadata } from 'components/common-form/hooks/useFieldMetadata';
import type { TagOption } from 'components/shared/Tags/model';
import { UnderlyingsFieldMetadata } from 'models/forms/fields/UnderlyingsFieldMetadata';
import type { Underlying } from 'models/underlying';
import type { UnderlyingCodeType } from 'models/underlying/base';
import { UnderlyingSearch } from 'models/underlying/underlying-search';
import { useCallback } from 'react';
import { lastValueFrom } from 'rxjs';
import { distinct } from 'utils/array/array';
import { httpClient } from 'utils/http.old/httpClient';
import { translateUnderlyingToTagOption } from 'utils/underlying/translate-underlying-to-tag-option';

const underlyingApi = underlyingsApi(httpClient);

export function useUnderlyingSearch(
  { clientId, productSubtype, productType, instrumentName, instrumentContractName, wrapper }: FormGlobalInfo,
  underlyingBlock?: string,
): UnderlyingSearch {
  const search = useCallback(
    async (
      input: string,
      selectedUnderlyingsIds: string[],
      selectedUnderlyings: ReadonlyArray<Underlying>,
      defaultUnderlyingCodeType: UnderlyingCodeType,
    ): Promise<ReadonlyArray<TagOption<Underlying>>> => {
      if (input.length < 3) {
        return [];
      }
      const { underlyings } = await lastValueFrom(
        underlyingApi.filter({
          basketUnderlyingIds: selectedUnderlyingsIds,
          basketUnderlyings: selectedUnderlyings,
          clientId: clientId || '',
          productSubtype,
          productType,
          underlyingBlock,
          instrumentName,
          instrumentContractName,
          query: input,
        }),
      );
      return underlyings.map(underlying => translateUnderlyingToTagOption(underlying, defaultUnderlyingCodeType));
    },
    [clientId, productSubtype, productType, instrumentName, instrumentContractName, underlyingBlock],
  );

  const getOptionsFromPastedLabels = useCallback(
    async (
      pastedLabels: string[],
      selectedUnderlyingsIds: string[],
      defaultUnderlyingCodeType: UnderlyingCodeType,
    ): Promise<ReadonlyArray<TagOption<Underlying>>> => {
      const distinctPastedLabels = pastedLabels.filter(distinct);
      const mappedUnderlyings = await lastValueFrom(
        underlyingApi.map({
          basketUnderlyingIds: selectedUnderlyingsIds,
          clientId: clientId || '',
          productSubtype,
          productType,
          underlyingsIds: distinctPastedLabels,
          instrumentName,
          instrumentContractName,
          wrapper,
          underlyingBlock,
        }),
      );
      return mappedUnderlyings.map<TagOption<Underlying>>((mappedUnderlying, index) =>
        mappedUnderlying
          ? translateUnderlyingToTagOption(mappedUnderlying, defaultUnderlyingCodeType)
          : { label: distinctPastedLabels[index], value: distinctPastedLabels[index] },
      );
    },
    [clientId, productSubtype, productType, instrumentName, instrumentContractName, wrapper, underlyingBlock],
  );
  return {
    getOptionsFromPastedLabels,
    search,
  };
}

export function useUnderlyingSearchByFormId(formId: string, fieldId: string | undefined) {
  const fieldMeta = useFieldMetadata<UnderlyingsFieldMetadata>(fieldId!);
  return useUnderlyingSearch(useFormGlobalInfo(formId), fieldMeta?.underlyingBlock);
}

export function useEssentialUnderlyingSearchByFormId(formId: string, underlyingBlock: string) {
  return useUnderlyingSearch(useFormGlobalInfo(formId), underlyingBlock);
}
